"use client"

import { useEffect, useState } from "react"
import { motion, AnimatePresence } from "framer-motion"
import { X } from "lucide-react"

interface AccessModeOnboardingModalProps {
    onAccept: () => void
    onDecline: () => void
}

const easyModeItems = ["Home", "Practice", "Quiz", "Mock Test", "Community", "Vocab", "Profile"]

export function AccessModeOnboardingModal({ onAccept, onDecline }: AccessModeOnboardingModalProps) {
    const [step, setStep] = useState<"idle" | "visible" | "accepting" | "declining">("idle")

    useEffect(() => {
        const t = setTimeout(() => setStep("visible"), 600)
        return () => clearTimeout(t)
    }, [])

    const handleAccept = () => {
        setStep("accepting")
        setTimeout(() => onAccept(), 300)
    }

    const handleDecline = () => {
        setStep("declining")
        setTimeout(() => onDecline(), 250)
    }

    return (
        <AnimatePresence>
            {(step === "visible" || step === "accepting" || step === "declining") && (
                <>
                    {/* Backdrop */}
                    <motion.div
                        key="backdrop"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        transition={{ duration: 0.2 }}
                        className="fixed inset-0 z-[9998] bg-black/50 backdrop-blur-sm"
                        onClick={handleDecline}
                    />

                    {/* Modal card */}
                    <motion.div
                        key="modal"
                        initial={{ opacity: 0, scale: 0.96, y: 16 }}
                        animate={
                            step === "visible"
                                ? { opacity: 1, scale: 1, y: 0 }
                                : { opacity: 0, scale: 0.97, y: 8, transition: { duration: 0.2 } }
                        }
                        transition={{ type: "spring", stiffness: 300, damping: 26 }}
                        className="fixed inset-0 z-[9999] flex items-end sm:items-center justify-center p-0 sm:p-4 pointer-events-none"
                    >
                        <div className="pointer-events-auto w-full sm:max-w-md bg-background text-foreground rounded-t-2xl sm:rounded-2xl shadow-2xl border border-border overflow-hidden max-h-[90dvh] overflow-y-auto">

                            {/* Mobile drag-handle pill */}
                            <div className="flex justify-center pt-3 sm:hidden">
                                <div className="w-10 h-1.5 rounded-full bg-muted-foreground/30" />
                            </div>

                            <div className="relative p-6">
                                {/* Close button */}
                                <button
                                    onClick={handleDecline}
                                    aria-label="Dismiss"
                                    className="absolute top-4 right-4 p-1.5 rounded-full text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
                                >
                                    <X className="w-4 h-4" />
                                </button>

                                {/* Heading */}
                                <h2 className="text-xl font-semibold tracking-tight pr-8 mb-1.5">
                                    Choose how you want to start
                                </h2>
                                <p className="text-base text-muted-foreground mb-5">
                                    SAT Buddy has a lot of tools. Start simple and unlock everything whenever you&apos;re ready.
                                </p>

                                {/* Mode options */}
                                <div className="space-y-3 mb-6">
                                    <div className="rounded-lg border border-border p-4">
                                        <p className="font-medium mb-1">Easy Mode</p>
                                        <p className="text-sm text-muted-foreground">
                                            Just the essentials: {easyModeItems.join(", ")}.
                                        </p>
                                    </div>
                                    <div className="rounded-lg border border-border p-4">
                                        <p className="font-medium mb-1">Expert Mode</p>
                                        <p className="text-sm text-muted-foreground">
                                            Every feature unlocked.
                                        </p>
                                    </div>
                                </div>

                                {/* CTA buttons */}
                                <div className="flex flex-col sm:flex-row gap-2">
                                    <button
                                        onClick={handleAccept}
                                        className="flex-1 px-4 py-2.5 rounded-lg bg-primary text-primary-foreground text-sm font-medium hover:bg-primary/90 transition-colors"
                                    >
                                        Use Easy Mode
                                    </button>
                                    <button
                                        onClick={handleDecline}
                                        className="flex-1 px-4 py-2.5 rounded-lg border border-border text-foreground text-sm font-medium hover:bg-muted transition-colors"
                                    >
                                        Stay in Expert Mode
                                    </button>
                                </div>

                                <p className="text-xs text-muted-foreground text-center mt-4">
                                    Switch anytime from the Access Mode toggle in the top bar.
                                </p>
                            </div>
                        </div>
                    </motion.div>
                </>
            )}
        </AnimatePresence>
    )
}
